export type ScenarioTone = "positive" | "warning" | "neutral"

export type BusinessScenario = {
  id: "leader" | "sales" | "marketing" | "operations"
  role: string
  shortRole: string
  icon: string
  prompt: string
  sources: readonly string[]
  steps: readonly string[]
  agentSteps: readonly {
    title: string
    detail: string
    source: string
    toolCalls?: readonly {
      label: string
      value: string
      icon?: string
      status?: "connected" | "done" | "draft"
    }[]
  }[]
  artifact: {
    title: string
    updated: string
    summary: string
    rows: readonly {
      label: string
      value: string
      detail: string
      tone: ScenarioTone
    }[]
    nextAction: string
  }
}

export const businessScenarios: readonly BusinessScenario[] = [
  {
    id: "leader",
    role: "Руководителю",
    shortRole: "Руководитель",
    icon: "user-speak-rounded-linear",
    prompt:
      "Каждое утро собери главное: динамику продаж, просроченные задачи и сообщения, которые требуют моего решения.",
    sources: [
      "CRM: сделки и этапы",
      "Реестр задач: сроки и статусы",
      "Разрешённые рабочие сообщения",
    ],
    steps: [
      "Сверяет изменения с предыдущим периодом",
      "Отделяет срочное от информационного",
      "Собирает решения в один утренний брифинг",
    ],
    agentSteps: [
      {
        title: "Получаю свежие данные",
        detail: "Загружаю сделки, сроки задач и разрешённые рабочие сообщения.",
        source: "рабочие источники",
        toolCalls: [
          { label: "CRM", value: "read deals", icon: "database-linear" },
          {
            label: "Чат",
            value: "Пользователь 1 + 2",
            icon: "users-group-rounded-bold-duotone",
          },
        ],
      },
      {
        title: "Проверяю отклонения",
        detail: "Сравниваю текущий темп с предыдущим периодом и нахожу просрочки.",
        source: "сделки и задачи",
      },
      {
        title: "Выделяю решения руководителя",
        detail: "Убираю информационный шум и оставляю вопросы, где нужно действие.",
        source: "проверенный контекст",
        toolCalls: [
          {
            label: "Thread",
            value: "упомянуть владельцев",
            icon: "chat-round-dots-bold-duotone",
          },
        ],
      },
      {
        title: "Собираю утренний брифинг",
        detail: "Связываю каждый вывод с источником и следующим шагом.",
        source: "итоговый брифинг",
      },
    ],
    artifact: {
      title: "Утренний брифинг",
      updated: "На начало рабочего дня",
      summary:
        "Требуют внимания три зоны: темп продаж, просроченные задачи и сообщения без решения.",
      rows: [
        {
          label: "Продажи",
          value: "Темп ниже предыдущей недели",
          detail: "Вынесены сделки, где не назначен следующий шаг",
          tone: "warning",
        },
        {
          label: "Задачи",
          value: "Просрочки собраны по владельцам",
          detail: "Для каждой задачи указан блокер и необходимое решение",
          tone: "warning",
        },
        {
          label: "Сообщения",
          value: "Отобраны сообщения для решения",
          detail: "Каждый вопрос показан вместе с исходным контекстом",
          tone: "neutral",
        },
      ],
      nextAction: "Разобрать сделки, затем блокеры и важные сообщения",
    },
  },
  {
    id: "sales",
    role: "Продажам",
    shortRole: "Продажи",
    icon: "hand-shake-linear",
    prompt:
      "Подготовь меня к звонку: что изменилось у клиента, о чём мы говорили раньше и с какой гипотезой лучше идти на встречу.",
    sources: [
      "Сайт клиента: публичные страницы",
      "Открытые новости компании",
      "CRM: история контактов и сделок",
    ],
    steps: [
      "Собирает свежие факты о компании",
      "Сопоставляет их с историей отношений",
      "Готовит вопросы и гипотезу разговора",
    ],
    agentSteps: [
      {
        title: "Изучаю компанию",
        detail: "Проверяю публичный сайт, новости и свежие упоминания перед встречей.",
        source: "сайт и новости",
        toolCalls: [
          { label: "tavily_search", value: "новости клиента", icon: "magnifer-linear" },
        ],
      },
      {
        title: "Поднимаю историю контактов",
        detail: "Нахожу последнюю встречу, договорённости и открытые сделки.",
        source: "история клиента",
      },
      {
        title: "Сверяю контекст",
        detail: "Отделяю новые факты от уже известных команде продаж.",
        source: "найденные факты",
      },
      {
        title: "Готовлю сценарий звонка",
        detail: "Формирую гипотезу, вопросы и безопасный следующий шаг.",
        source: "бриф к звонку",
      },
    ],
    artifact: {
      title: "Бриф к звонку",
      updated: "Перед встречей",
      summary:
        "Для звонка собраны изменения у клиента, история договорённостей и рабочая гипотеза разговора.",
      rows: [
        {
          label: "Что изменилось",
          value: "Изменилось позиционирование предложения",
          detail: "Сигнал найден на публичных страницах компании",
          tone: "neutral",
        },
        {
          label: "История",
          value: "Последний разговор был о запуске",
          detail: "Подняты договорённости и открытые вопросы из истории клиента",
          tone: "positive",
        },
        {
          label: "Гипотеза",
          value: "Предложить узкий пилот",
          detail: "На звонке уточнить критерий успеха и владельца процесса",
          tone: "positive",
        },
      ],
      nextAction: "Начать звонок с изменений, затем проверить гипотезу пилота",
    },
  },
  {
    id: "marketing",
    role: "Маркетингу",
    shortRole: "Маркетинг",
    icon: "chart-square-linear",
    prompt:
      "Сравни кампании за два периода, найди просадки и подготовь рекомендации, что проверить в первую очередь.",
    sources: ["Рекламный кабинет", "Веб-аналитика", "Целевые показатели"],
    steps: [
      "Приводит отчёты к единому периоду",
      "Сравнивает расходы, отклик и конверсию",
      "Ранжирует гипотезы по ожидаемому влиянию",
    ],
    agentSteps: [
      {
        title: "Получаю данные кампаний",
        detail:
          "Проверяю периоды, расходы, отклик и конверсию в подключённых источниках.",
        source: "рекламный кабинет",
        toolCalls: [
          {
            label: "Ads",
            value: "campaign metrics",
            icon: "chart-square-linear",
          },
        ],
      },
      {
        title: "Привожу данные к одной базе",
        detail: "Сопоставляю кампании и исключаю несравнимые строки.",
        source: "два периода",
      },
      {
        title: "Ищу точки просадки",
        detail: "Сверяю отклик и конверсию с согласованными ориентирами.",
        source: "целевые показатели",
      },
      {
        title: "Ранжирую проверки",
        detail: "Сначала выношу гипотезы, которые можно подтвердить исходными данными.",
        source: "разбор кампаний",
      },
    ],
    artifact: {
      title: "Разбор кампаний",
      updated: "Сравнение двух периодов",
      summary:
        "Сравнение выделило стабильный поиск, просадку ретаргетинга и данные, которые нужно перепроверить.",
      rows: [
        {
          label: "Кампания поиска",
          value: "Результат стабилен между периодами",
          detail: "Сохранить настройки и отдельно оценить потенциал масштабирования",
          tone: "positive",
        },
        {
          label: "Ретаргетинг",
          value: "Результат снизился относительно ориентира",
          detail: "В первую очередь проверить аудитории и частоту показов",
          tone: "warning",
        },
        {
          label: "Контент",
          value: "Сравнение пока неоднозначно",
          detail: "Сверить атрибуцию до любых изменений бюджета",
          tone: "neutral",
        },
      ],
      nextAction: "Проверить ретаргетинг, затем подтвердить атрибуцию контента",
    },
  },
  {
    id: "operations",
    role: "Операциям",
    shortRole: "Операции",
    icon: "clipboard-list-linear",
    prompt:
      "Найди просроченные задачи и блокеры, затем собери конкретный список действий для команды на сегодня.",
    sources: [
      "Реестр задач: сроки и владельцы",
      "Рабочие сообщения: блокеры команды",
      "Календарь команды: свободные слоты",
    ],
    steps: [
      "Фильтрует задачи с нарушенным сроком",
      "Связывает блокеры с владельцами",
      "Формирует очередь действий по приоритету",
    ],
    agentSteps: [
      {
        title: "Фильтрую просроченные задачи",
        detail: "Проверяю сроки, статусы и владельцев в рабочем реестре.",
        source: "реестр задач",
        toolCalls: [
          { label: "Tasks", value: "project board", icon: "database-linear" },
          { label: "Messages", value: "blockers", icon: "chat-round-dots-bold-duotone" },
        ],
      },
      {
        title: "Нахожу блокеры",
        detail: "Сканирую последние сообщения и связываю зависимости между задачами.",
        source: "рабочие сообщения",
        toolCalls: [
          {
            label: "Messages",
            value: "командные обсуждения",
            icon: "chat-round-dots-bold-duotone",
          },
        ],
      },
      {
        title: "Сверяю свободные слоты",
        detail: "Нахожу ближайшее окно для короткой встречи по блокеру.",
        source: "календарь команды",
        toolCalls: [
          { label: "Calendar", value: "свободное окно", icon: "calendar-bold-duotone" },
        ],
      },
      {
        title: "Формирую очередь действий",
        detail: "Группирую действия по владельцам и порядку разблокировки.",
        source: "план на сегодня",
      },
    ],
    artifact: {
      title: "Очередь действий",
      updated: "План на сегодня",
      summary:
        "Просрочки собраны в очередь: сначала снять внешний блокер, затем назначить владельцев и обновить сроки.",
      rows: [
        {
          label: "Критично",
          value: "[Задачи] Просроченная задача",
          detail: "Назначить владельца и обновить следующий шаг",
          tone: "warning",
        },
        {
          label: "Сегодня",
          value: "[Сообщения] Блокер от команды",
          detail: "Нужен ответ по входным данным до конца дня",
          tone: "neutral",
        },
        {
          label: "После разблокировки",
          value: "[Календарь] Окно для встречи",
          detail: "Короткая синхронизация владельцев зависимых задач",
          tone: "positive",
        },
      ],
      nextAction: "Отправить владельцам очередь действий на сегодня",
    },
  },
] as const
